import React from 'react';
import { Policies } from '../../../Policies';
import { QuoteSearch } from '../../../QuoteSearch';
import { Admin } from '../../../Admin';
import { BlockList } from 'BlockList';
import { NavigationItem } from '../../types';
import { RouteComponentProps } from 'react-router';
import {
  manageBlockListPolicy,
  viewAdminPortalPolicy,
} from '../../authPolicies';
import { CurrentUserData } from 'Auth/components/AuthProvider/AuthProvider';
import { RewardsEnrollmentProviderType } from 'Shared/hooks';
import { IncentiveProgramEnrollmentProviderType } from 'Shared/hooks/useIncentiveProgramEnrollment';
import { Rewards } from 'Rewards/components';
import { hasScopedPermission } from 'Shared/utils';
import { BoltIcon } from '@clearcover/atlas';
import { Tools } from 'Tools';
import { env } from 'Shared/constants/env';

const agentServicesRoute = {
  name: 'Admin',
  path: '/admin',
  component: Admin,
  assetVariant: 'people',
  testId: 'primary-menu-admin-link',
} as const;

const blockListRoute = {
  name: 'Block List',
  path: '/block-list',
  component: BlockList,
  assetVariant: 'shieldOff',
  testId: 'primary-menu-block-list-link',
} as const;

const navItems = [
  {
    name: 'Quotes',
    path: '/quotes',
    component: QuoteSearch,
    assetVariant: 'page',
    testId: 'primary-menu-quotes-link',
  } as const,
  {
    name: 'Policies',
    path: '/policies',
    assetVariant: 'shield',
    component: Policies,
    testId: 'primary-menu-policies-link',
  } as const,
];

const rewardsRoute = {
  name: 'Rewards',
  path: '/rewards',
  assetVariant: 'gift',
  component: Rewards,
  testId: 'primary-menu-rewards-link',
} as const;

const toolsRoute = {
  name: 'Tools',
  path: '/tools',
  component: Tools,
  icon: BoltIcon,
  testId: 'primary-menu-tools-link',
} as const;

type NavigationManifestType = Array<
  NavigationItem & {
    component: React.FunctionComponent<RouteComponentProps>;
  }
>;

export const getRoutes: ({
  user,
  rewardsEnrollment,
  incentiveProgramEnrollment,
}: {
  user: CurrentUserData['currentUser'];
  rewardsEnrollment?: RewardsEnrollmentProviderType;
  incentiveProgramEnrollment?: IncentiveProgramEnrollmentProviderType;
}) => NavigationManifestType = ({
  user,
  rewardsEnrollment,
  incentiveProgramEnrollment,
}) => {
  const canViewAdminPortal = viewAdminPortalPolicy(user.effectiveGrants);
  const canManageBlockList = manageBlockListPolicy(user.effectiveGrants);
  // Gate incentive-program enrollment on the kill switch.  When
  // rewardsSpinEnabled is OFF, a 26For26-only agent should see no Rewards tab
  // at all — treat their incentive enrollment as absent for nav purposes.
  const enrolledInIncentiveProgram =
    env.rewardsSpinEnabled &&
    incentiveProgramEnrollment &&
    !incentiveProgramEnrollment.incentiveProgramEnrollmentLoading &&
    (incentiveProgramEnrollment.incentiveProgramEnrollmentResponse
      ?.isEnrolled ||
      incentiveProgramEnrollment.incentiveProgramEnrollmentResponse?.status ===
        'TC_PENDING');
  const enrolledInLegacyRewards =
    rewardsEnrollment &&
    !rewardsEnrollment.rewardsEnrollmentLoading &&
    rewardsEnrollment.rewardsEnrollmentResponse?.isEnrolled;
  // Keep the rewards route mounted while either enrollment check is still
  // in flight — otherwise the user clicks /rewards, the Switch doesn't find
  // a matching Route, and the default <Redirect to="/quotes"> fires before
  // the enrollment fetch settles.
  // incentiveEnrollmentPending is also gated on the kill switch: when the
  // flag is OFF the incentive fetch result is irrelevant, so pending state
  // should not hold the tab open.
  const incentiveEnrollmentPending =
    env.rewardsSpinEnabled &&
    (incentiveProgramEnrollment?.incentiveProgramEnrollmentLoading ?? false);
  const legacyRewardsEnrollmentPending =
    rewardsEnrollment?.rewardsEnrollmentLoading ?? false;
  const canViewRewards =
    enrolledInIncentiveProgram ||
    enrolledInLegacyRewards ||
    incentiveEnrollmentPending ||
    legacyRewardsEnrollmentPending;

  const hasStateConfigurationsAccess = hasScopedPermission(
    user.effectiveGrants,
    'partner',
    'state_configurations.read'
  );
  const hasMoratoriumsAccess = hasScopedPermission(
    user.effectiveGrants,
    'partner',
    'moratoriums.read'
  );
  const canViewTools = hasStateConfigurationsAccess || hasMoratoriumsAccess;

  return [
    ...navItems,
    ...(canManageBlockList ? [blockListRoute] : []),
    ...(canViewAdminPortal ? [agentServicesRoute] : []),
    ...(canViewRewards ? [rewardsRoute] : []),
    ...(canViewTools ? [toolsRoute] : []),
  ];
};
